import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { UnifiedNavigation } from '@/components/UnifiedNavigation'
import { Footer } from '@/components/Footer'
import HeroSection from '@/components/gcc/HeroSection'
import CoreFeaturesSection from '@/components/gcc/CoreFeaturesSection'
import TargetAudienceSection from '@/components/gcc/TargetAudienceSection'
import ContentSection from '@/components/gcc/ContentSection'
import DifferentiatorsSplitSection from '@/components/gcc/DifferentiatorsSplitSection'
import { gccLandingPageData } from '@/lib/gcc-content'
import { useEffect, useState } from 'react'
import { getMyProviderProfile } from '@/services/providers'
import { useUser } from '@clerk/clerk-react'
import { FadeIn, SlideUp } from '@/components/animations'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'

const GCCPlatformPage = () => {
  const [hasProfile, setHasProfile] = useState(false)
  const { isLoaded: isUserLoaded, isSignedIn } = useUser()
  useEffect(() => {
    if (!isUserLoaded) return
    if (!isSignedIn) {
      setHasProfile(false)
      return
    }
    ;(async () => {
      try {
        const p = await getMyProviderProfile()
        setHasProfile(Boolean(p))
      } catch {
        setHasProfile(false)
      }
    })()
  }, [isUserLoaded, isSignedIn])
  const { 
    hero, 
    whySection, 
    coreFeatures, 
    targetAudiences, 
    differentiators, 
    joinSection 
  } = gccLandingPageData

  return (
    <div className="min-h-screen w-full">
      {/* Header */}
      <UnifiedNavigation currentPage="aifirstgcc" />
      
      <main>
        {/* Hero Section */}
        <HeroSection hero={hero} />
      
      {/* Why This Platform Section */}
      <ContentSection section={whySection} />
      
      {/* Core Features Section */}
      <CoreFeaturesSection features={coreFeatures} />
      
      {/* Target Audiences Section */}
      <TargetAudienceSection audiences={targetAudiences} />
      
      {/* What Makes Us Different Section - split layout */}
      <DifferentiatorsSplitSection section={differentiators} />
      
      {/* Join the Movement Section */}
      <section
        data-testid="join-movement"
        className="py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      >
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h2 className="text-3xl font-light text-white mb-6 lg:text-4xl tracking-tight">
              {joinSection.title}
            </h2>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="text-xl text-slate-300 mb-12 leading-relaxed font-light max-w-3xl mx-auto">
              {joinSection.subtitle}
            </p>
          </FadeIn>

          <SlideUp delay={0.4} distance={20}>
            <div className="flex justify-center">
              {hasProfile ? (
                <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-50 shadow-lg rounded-lg px-8 py-3 font-medium tracking-tight transition-all duration-300">
                  <Link to="/provider">Profile</Link>
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="lg"
                      className="bg-white text-slate-900 hover:bg-slate-50 shadow-lg rounded-lg px-8 py-3 font-medium tracking-tight transition-all duration-300"
                    >
                      Join Today
                      <ChevronDown className="ml-2 w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="min-w-[200px]">
                    <DropdownMenuItem asChild>
                      <Link
                        to="/auth?mode=signup&role=provider&next=/get-started"
                        className="cursor-pointer"
                      >
                        Join as Provider
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/auth?mode=signup&role=gcc&next=/gcc-dashboard"
                        className="cursor-pointer"
                      >
                        Join as GCC
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </SlideUp>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  )
}

export default GCCPlatformPage
